import { format, isFuture, isToday } from 'date-fns';
import { BasketballSDKOptions, FootballSDKOptions, SDKDateOptions, TennisSDKOptions } from '../../types';
import { defaultLocaleLang, defaultTimezone } from '../../constants/date';
import { bg, de, el, enUS, hu, it, pt, ro, sk, srLatn, tr } from 'date-fns/locale';

// convert date to selected lang
export const getDateFnsLocale = (lang: string | undefined | null): Locale => {
  switch (lang) {
    case 'bg':
      return bg;
    case 'el':
      return el;
    case 'ro':
      return ro;
    case 'tr':
      return tr;
    case 'de':
      return de;
    case 'pt':
      return pt;
    case 'it':
      return it;
    case 'sr':
      return srLatn;
    case 'hu':
      return hu;
    case 'sk':
      return sk;
    default:
      return enUS;
  }
};

// convert date to selected timezone
export const convertTZ = (date: Date | string, tzString: string): Date => {
  return new Date((typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', { timeZone: tzString }));
};

export const getDate = (date: string, sdkDateOptions?: SDKDateOptions): [boolean, string] => {
  const localeLang = sdkDateOptions?.dataConfigLang ?? defaultLocaleLang;

  const kickoffTime = convertDateToTz(date, sdkDateOptions);

  // Check if the date is not in the past
  const isKickOffFuture = isFuture(kickoffTime);
  const shouldShowDate = isKickOffFuture && !isToday(kickoffTime);

  // Format date with correct language (months should be translated to the passed lang from sdk)
  return [shouldShowDate, format(kickoffTime, 'dd MMM', { locale: getDateFnsLocale(localeLang) }).toUpperCase()];
};

export const getMatchDate = (date: string | Date, sdkDateOptions?: SDKDateOptions, formatType?: string) => {
  // Get sdk passed language
  const localeLang = sdkDateOptions?.dataConfigLang ?? defaultLocaleLang;

  const kickoffTime = convertDateToTz(date, sdkDateOptions);

  return format(kickoffTime, formatType ?? 'dd MMMM yyyy', { locale: getDateFnsLocale(localeLang) });
};

// will return result in format (14/07/2024 - 22:00 GMT+3)
export const getMatchDateExtended = (date: string | Date, sdkDateOptions: SDKDateOptions, formatType = 'dd/MM/yyyy - HH:mm') => {
  const { dataConfigLang, dataConfigTimezone } = sdkDateOptions;
  const dateObject = typeof date === 'string' ? new Date(date) : date;
  const dateFormatter = new Intl.DateTimeFormat(dataConfigLang, {
    timeZone: dataConfigTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const timeZoneFormatter = new Intl.DateTimeFormat(dataConfigLang, {
    timeZone: dataConfigTimezone,
    timeZoneName: 'short',
  });

  // Format the date and time
  const formattedParts = dateFormatter.formatToParts(dateObject);
  const dateParts = {
    day: formattedParts.find((part) => part.type === 'day')?.value || '01',
    month: formattedParts.find((part) => part.type === 'month')?.value || '01',
    year: formattedParts.find((part) => part.type === 'year')?.value || '1970',
    hour: formattedParts.find((part) => part.type === 'hour')?.value || '00',
    minute: formattedParts.find((part) => part.type === 'minute')?.value || '00',
  };

  // Construct the formatted string based on the formatType
  return formatType
    .replace('dd', dateParts.day)
    .replace('MM', dateParts.month)
    .replace('yyyy', dateParts.year)
    .replace('HH', dateParts.hour)
    .replace('mm', dateParts.minute);
};

export const formatGameTime = (dateTime: string | undefined | null | number, sdkDateOptions?: SDKDateOptions) => {
  const defaultReturnValue = '-';
  if (typeof dateTime === 'string') {
    const dateHoursToTimezone = dateTime && convertTZ(new Date(dateTime), sdkDateOptions?.dataConfigTimezone ?? defaultTimezone);
    return dateHoursToTimezone ? format(dateHoursToTimezone, 'H:mm') : defaultReturnValue;
  }

  return defaultReturnValue;
};

export const extractOnlyDateSDKOptions = (
  generalSDKOptions: FootballSDKOptions | BasketballSDKOptions | TennisSDKOptions,
): SDKDateOptions => {
  return {
    dataConfigTimezone: generalSDKOptions?.dataConfigTimezone ?? defaultTimezone,
    dataConfigLang: generalSDKOptions?.dataConfigLang ?? defaultLocaleLang,
  };
};

export const convertDateToTz = (date: string | Date, sdkDateOptions?: SDKDateOptions) => {
  // Get sdk passed timezone
  const timeZone = sdkDateOptions?.dataConfigTimezone ?? defaultTimezone;

  // Convert to selected timezone
  return convertTZ(date, timeZone);
};

// TODO: temporary workaround related to tsup issue when importing dayjs module
export const getValidatedDate = (dayjs: any, date?: string, dateFormat?: string) => {
  const formatedDate = date && dayjs(date, dateFormat).isValid() ? dayjs(date, dateFormat).toDate() : null;
  return formatedDate ? format(formatedDate, 'yyyy-MM-dd') : '';
};

// TODO: temporary workaround related to tsup issue when importing dayjs module
export const formatDateWithAge = (dayjs: any, birthdate: string, dataDateFormat?: string): string => {
  if (!birthdate) {
    return '-';
  }

  const date = dayjs(birthdate, 'YYYY-MM-DD');
  const now = dayjs();

  const age = now.year() - date.year();
  const adjustedAge = now.isBefore(date.add(age, 'year')) ? age - 1 : age;

  const format = dataDateFormat && dataDateFormat.length > 0 ? normalizeDateFormat(dataDateFormat) : 'DD.MM.YYYY';
  const formattedDate = date.format(format);

  return `${adjustedAge} (${formattedDate})`;
};

// Helper function to normalize date format
const normalizeDateFormat = (format: string): string => {
  return format.replace(/yyyy/g, 'YYYY').replace(/dd/g, 'DD').replace(/mm/g, 'MM');
};
